import NextLink from 'next/link';
import type { AnchorHTMLAttributes, ReactNode, RefObject } from 'react';

import type { Theme } from '@/app/types/styles/theme';
import { cn } from '@/app/utils/cn';

export interface LinkProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
> {
  ref?: RefObject<HTMLAnchorElement | null>;
  href: string;
  /**
   * - `null`: Prefetch behavior depends on whether the route is static or dynamic. For static routes, the full route will be prefetched (including all its data). For dynamic routes, the partial route down to the nearest segment with a loading.js boundary will be prefetched.
   * - `true`: The full route will be prefetched for both static and dynamic routes.
   * - `false` (default): Prefetching will never happen both on entering the viewport and on hover.
   */
  prefetch?: boolean | null;
  scroll?: boolean;
  'aria-label'?: string;
  children?: ReactNode;
  theme?: Theme;
  className?: string;
}

export const Link = ({
  ref,
  prefetch,
  scroll,
  'aria-label': ariaLabel,
  children,
  href,
  target,
  theme,
  className,
  ...rest
}: LinkProps) => {
  return (
    <NextLink
      ref={ref}
      href={href}
      prefetch={prefetch === undefined ? false : prefetch}
      scroll={scroll}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      aria-label={ariaLabel}
      className={cn(
        {
          'link-light': theme === 'light',
          'link-dark': theme === 'dark',
        },
        className
      )}
      {...rest}
    >
      {children}
    </NextLink>
  );
};
